/**
 * 月次集計のバケット（月）と実日付の相互変換（FKV3-13085）
 *
 * 締日 d（1〜27）のとき、1バケットは **(d+1)日 〜 翌月d日**。締日が未設定なら暦月。
 *
 * バケットを何月と呼ぶかは **開始日の月** に揃えている。
 * 業績ダッシュボード・マイチャートの x 軸が開始日の月を表示しており（owen の
 * `format(startDate, 'YYYY/M')`）、hibari の月キーも `DATE_TRUNC` の開始月であるため。
 * 商習慣の「4月度＝3/21〜4/20」とは1ヶ月ずれるので、締日ありのときは
 * `monthLabel` で必ず実日付を併記する。
 */

/** `YYYY-MM` 形式の月。`<input type="month">` の値と同じ形式 */
export type MonthKey = string & { readonly __brand: 'MonthKey' };

/** `YYYY-MM-DD` 形式の日付 */
type DateString = string;

const pad2 = (n: number) => String(n).padStart(2, '0');

const toMonthKey = (year: number, month1to12: number) => `${year}-${pad2(month1to12)}` as MonthKey;

const toDateString = (year: number, month1to12: number, day: number): DateString =>
  `${year}-${pad2(month1to12)}-${pad2(day)}`;

/** `YYYY-MM` を年と月（1〜12）に分解する */
function parseMonthKey(monthKey: MonthKey): { year: number; month: number } {
  const [year, month] = monthKey.split('-').map(Number);
  return { year, month };
}

/** 指定した年月の翌月を返す（12月は翌年1月） */
function nextMonth(year: number, month: number): { year: number; month: number } {
  return month === 12 ? { year: year + 1, month: 1 } : { year, month: month + 1 };
}

/** 指定した年月の日数（月末日）を返す */
function lastDayOfMonth(year: number, month: number): number {
  // Date.UTC の day に 0 を渡すと前月末日になる
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** 月バケットの開始日 */
export function monthBucketStart(monthKey: MonthKey, closingDate: number | null): DateString {
  const { year, month } = parseMonthKey(monthKey);
  return toDateString(year, month, closingDate === null ? 1 : closingDate + 1);
}

/** 月バケットの終了日 */
export function monthBucketEnd(monthKey: MonthKey, closingDate: number | null): DateString {
  const { year, month } = parseMonthKey(monthKey);
  if (closingDate === null) {
    return toDateString(year, month, lastDayOfMonth(year, month));
  }
  const next = nextMonth(year, month);
  return toDateString(next.year, next.month, closingDate);
}

/** 日付が属する月バケットを返す（SQL の月丸めと同じ規則） */
export function monthKeyOfDate(date: DateString, closingDate: number | null): MonthKey {
  const [year, month, day] = date.split('-').map(Number);
  if (closingDate === null || day > closingDate) {
    return toMonthKey(year, month);
  }
  // 締日以前は前月のバケットに属する
  return month === 1 ? toMonthKey(year - 1, 12) : toMonthKey(year, month - 1);
}

/** 画面に出す月の表示名。締日ありのときは実期間を併記する */
export function monthLabel(monthKey: MonthKey, closingDate: number | null): string {
  const { year, month } = parseMonthKey(monthKey);
  const name = `${year}年${month}月`;
  if (closingDate === null) return name;
  const start = monthBucketStart(monthKey, closingDate).replace(/-/g, '/');
  const end = monthBucketEnd(monthKey, closingDate).replace(/-/g, '/');
  return `${name}（${start}〜${end}）`;
}
